import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FailedPaymentError } from '../payment/failed-payment-error';
import {
  deserializeCashBoxAddTransaction,
  deserializeCashBoxChangePayload,
  deserializeProductReturnAttributesPayload,
  deserializeProductReturnsPayload,
  deserializeUpdateCartPayload,
} from './converters/deserializers';
import {
  serializePaginatedTransactions,
  serializeTransaction,
} from './converters/transaction-history.serializers';
import {
  serializeBarcodes,
  serializeCart,
  serializeCartCustomerUsers,
  serializeCashBox,
  serializeCollection,
  serializePaginatedCollection,
  serializeEndOfDay,
  serializePaginatedProducts,
  serializePaymentAuthorization,
  serializePaymentRefund,
  serializePaymentResult,
  serializePaymentReversal,
  serializePickedUpResponse,
  serializePrinterStatus,
  serializeProduct,
  serializeProductReturnsResult,
  serializeProductReturnsValidationResult,
  serializeProducts,
  serializeReservationsAndPickups,
  serializeReservationsAndPickupsCount,
  serializeSettings,
} from './converters/types';
import {
  AuthorizationResponse,
  Barcodes,
  Cart,
  CartCustomerUser,
  CashBox,
  CashBoxChangePayload,
  CashBoxTransactionPayload,
  Collection,
  CompletedPayment,
  EndOfDayResponse,
  PaginatedCollection,
  PaginatedProducts,
  PaginatedTransactions,
  PaymentSubmitPayload,
  PickedUpResponse,
  PickupsAndReservations,
  PickupsAndReservationsCount,
  PrinterStatus,
  Product,
  ProductReturnAttributesPayload,
  ProductReturnsPayload,
  ProductReturnsResult,
  ProductReturnsValidationResult,
  RefundResponse,
  ReversalResponse,
  Settings,
  Transaction,
  UpdateCartPayload,
  ReservationProduct,
} from './models';
import { UrlService } from './url.service';
import { EndpointErrorsDescriptions } from './endpoint-error-descriptions';


@Injectable({ providedIn: 'root' })
export class BackendService {
  private get BASE_URL(): string {
    return this.url.API_URL;
  }
  private get EXTERNAL_API_URL(): string {
    return this.url.EXTERNAL_API_URL;
  }

  constructor(private readonly http: HttpClient, private readonly url: UrlService) {}

  fetchSettings(): Promise<Settings> {
    const url = encodeURI(`${this.BASE_URL}/settings`);
    return firstValueFrom(this.http.get(url)).then(serializeSettings).catch(reason => {
      throw new BackendServiceError(EndpointErrorsDescriptions.SETTINGS, reason);
    });
  }

  fetchAllProducts(): Promise<Product[]> {
    const url = encodeURI(`${this.BASE_URL}/products`);
    return firstValueFrom(this.http.get(url)).then(serializeProducts).catch(reason => {
      throw new BackendServiceError(EndpointErrorsDescriptions.PRODUCTS_LIST, reason);
    });
  }

  fetchPaginatedProducts(params?: { [key: string]: string | number }): Promise<PaginatedProducts> {
    const url = encodeURI(`${this.BASE_URL}/products`);
    return firstValueFrom(this.http.get(url, { params: params ?? {p: 1} }))
      .then(serializePaginatedProducts).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.PRODUCTS_LIST, reason);
      });
  }

  fetchPaginatedPickupsAndReservations(params?: { [key: string]: string | number }): Promise<PickupsAndReservations> {
    const url = encodeURI(`${this.BASE_URL}/pickups`);
    return firstValueFrom(this.http.get(url, { params: params ?? {p: 1} }))
      .then(serializeReservationsAndPickups).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.PICKUPS, reason);
      });
  }

  fetchPaginatedPickedups(params?: { [key: string]: string | number }): Promise<PickupsAndReservations> {
    const url = encodeURI(`${this.BASE_URL}/pickups/completed`);
    return firstValueFrom(this.http.get(url, { params: params ?? {p: 1} }))
      .then(serializeReservationsAndPickups).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.PICKUPS_LIST, reason);
      });
  }

  fetchProduct(id: number): Promise<Product> {
    const url = encodeURI(`${this.BASE_URL}/products/${id}`);
    return firstValueFrom(this.http.get(url))
      .then(serializeProduct).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.PRODUCTS, reason);
      });
  }

  fetchCollection(collectionId: number): Promise<Collection> {
    const url = encodeURI(`${this.BASE_URL}/collections/${collectionId}`);
    return firstValueFrom(this.http.get(url))
      .then(serializeCollection).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.COLLECTIONS, reason);
      });
  }

  fetchPaginatedCollection(collectionId: number, params?: { [key: string]: string | number }): Promise<PaginatedCollection> {
    const url = encodeURI(`${this.BASE_URL}/collections/${collectionId}`);
    return firstValueFrom(this.http.get(url, { params: params ?? {p: 1} }))
      .then(serializePaginatedCollection).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.COLLECTIONS, reason);
      });
  }

  fetchBarcodes(): Promise<Barcodes> {
    const url = encodeURI(`${this.BASE_URL}/barcodes`);
    return firstValueFrom(this.http.get(url))
      .then(serializeBarcodes).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.BARCODES, reason);
      });
  }

  fetchCart(): Promise<Cart> {
    const url = encodeURI(`${this.BASE_URL}/cart`);
    return firstValueFrom(this.http.get(url, { withCredentials: true }))
      .then(serializeCart)
      .then(res => res.cart).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CART, reason);
      });
  }

  fetchCashBox(id: string): Promise<CashBox> {
    const url = encodeURI(`${this.BASE_URL}/cash-box/${id}`);
    return firstValueFrom(this.http.get(url))
      .then(serializeCashBox).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CASH_BOX, reason);
      });
  }

  openCashBox(id: string, payload: CashBoxChangePayload): Promise<CashBox> {
    const url = encodeURI(`${this.BASE_URL}/cash-box/${id}/open`);
    return firstValueFrom(this.http.post(url, deserializeCashBoxChangePayload(payload)))
      .then(serializeCashBox).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CASH_BOX_OPEN, reason);
      });
  }

  closeCashBox(id: string, payload: CashBoxChangePayload): Promise<CashBox> {
    const url = encodeURI(`${this.BASE_URL}/cash-box/${id}/close`);
    return firstValueFrom(this.http.post(url, deserializeCashBoxChangePayload(payload)))
      .then(serializeCashBox).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CASH_BOX_CLOSE, reason);
      });
  }

  openCashDrawer(deviceId: string) {
    const url = encodeURI(`${this.EXTERNAL_API_URL}/queue_open_cash_drawer`);
    return firstValueFrom(this.http.post(url, { device_id: deviceId })).catch(reason => {
      throw new BackendServiceError(EndpointErrorsDescriptions.OPEN_CASH_DRAWER, reason);
    });
  }

  openPrinterboxCashDrawer(deviceId: string) {
    const url = encodeURI(`${this.EXTERNAL_API_URL}/push_printbox_drawer`);
    return firstValueFrom(this.http.post(url, { device_id: deviceId })).catch(reason => {
      throw new BackendServiceError(EndpointErrorsDescriptions.PUSH_PRINTBOX_DRAWER, reason);
    });
  }

  updateCart(payload: UpdateCartPayload) {
    const url = encodeURI(`${this.BASE_URL}/cart/update`);
    return firstValueFrom(this.http.post(url, deserializeUpdateCartPayload(payload), { withCredentials: true }))
      .then(serializeCart)
      .then(res => res.cart).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CART_UPDATE, reason);
      });
  }

  clearCart() {
    const url = encodeURI(`${this.BASE_URL}/cart/clear`);
    return firstValueFrom(this.http.post(url, undefined, { withCredentials: true }))
      .then(serializeCart)
      .then(res => res.cart).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CART_CLEAR, reason);
      });
  }

  addToCart(productId: number) {
    const url = encodeURI(`${this.BASE_URL}/cart/add/${productId}`);
    return firstValueFrom(this.http.post(url, undefined, { withCredentials: true }))
      .then(serializeCart)
      .then(res => res.cart).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CART_ADD_PRODUCT, reason);
      });
  }

  async checkoutCart(payload: PaymentSubmitPayload): Promise<CompletedPayment> {
    const url = encodeURI(`${this.BASE_URL}/cart/checkout`);
    const result = await firstValueFrom(this.http.post(url, payload, { withCredentials: true }))
      .then(serializePaymentResult).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CART_CHECKOUT, reason);
      });

    if (result.status === 'failed') {
      throw new FailedPaymentError(result.error);
    }

    return result;
  }

  fetchPaginatedTransactionHistory(posUid: string, params?: { [key: string]: string | number }): Promise<PaginatedTransactions> {
    const url = encodeURI(`${this.BASE_URL}/cash-box/${posUid}/transactions`);
    return firstValueFrom(this.http.get(url, { params: params ?? {p: 1} }))
      .then(serializePaginatedTransactions).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CASHBOX_TRANSACTIONS_LIST, reason);
      });
  }

  fetchTransactionHistoryRecord(posUid: string, transactionId: number): Promise<Transaction> {
    const url = encodeURI(`${this.BASE_URL}/cash-box/${posUid}/transactions/${transactionId}`);
    return firstValueFrom(this.http.get(url))
      .then(serializeTransaction).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CASHBOX_TRANSACTIONS, reason);
      });
  }

  addAdHocProductToCart(payload: { title: string; price: number; tax_rate: number }) {
    const url = encodeURI(`${this.BASE_URL}/cart/add_adhoc_product`);
    return firstValueFrom(this.http.post(url, payload, { withCredentials: true }))
      .then(serializeCart)
      .then(res => res.cart).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.ADHOC_PRODUCT_ADD, reason);
      });
  }

  validateReturnProducts(payload: ProductReturnsPayload): Promise<ProductReturnsValidationResult> {
    const url = encodeURI(`${this.BASE_URL}/returns/validate`);
    return firstValueFrom(this.http.post(url, deserializeProductReturnsPayload(payload)))
      .then(serializeProductReturnsValidationResult).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.PRODUCT_RETURNS_VALIDATE, reason);
      });
  }

  returnProducts(payload: ProductReturnsPayload): Promise<ProductReturnsResult> {
    const url = encodeURI(`${this.BASE_URL}/returns`);
    return firstValueFrom(this.http.post(url, deserializeProductReturnsPayload(payload)))
      .then(serializeProductReturnsResult).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.PRODUCT_RETURNS, reason);
      });
  }

  updateReturnProductsAttributes(transactionId: number, payload: ProductReturnAttributesPayload): Promise<any> {
    const url = encodeURI(`${this.BASE_URL}/returns/${transactionId}`);
    return firstValueFrom(this.http.put(url, deserializeProductReturnAttributesPayload(payload))).catch(reason => {
      throw new BackendServiceError(EndpointErrorsDescriptions.PRODUCT_RETURNS_TRANSACTION, reason);
    });
  }

  cashBoxAddTransaction(cacheBoxId: string, payload: CashBoxTransactionPayload): Promise<CashBox> {
    const url = encodeURI(`${this.BASE_URL}/cash-box/${cacheBoxId}/transactions/add`);
    return firstValueFrom(this.http.post(url, deserializeCashBoxAddTransaction(payload)))
      .then(serializeCashBox).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CASHBOX_ADD_TRANSACTION, reason);
      });
  }

  async searchCustomer(search?: string | null): Promise<CartCustomerUser[]> {
    const url = encodeURI(`${this.BASE_URL}/customers/search`);
    const params = !search ? null : { q: search };
    return firstValueFrom(this.http.get<unknown>(url, { params: params ?? {} }))
      .then(serializeCartCustomerUsers).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CUSTOMERS_SEARCH, reason);
      });
  }

  createCustomer(payload: object): Promise<CartCustomerUser[]> {
    const url = encodeURI(`${this.BASE_URL}/customers`);
    return firstValueFrom(this.http.post(url, payload))
      .then(serializeCartCustomerUsers).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CUSTOMERS, reason);
      }); // For some reason after successful creation api returns user in array
  }

  async fetchReservationsAndPickupsCount(): Promise<PickupsAndReservationsCount> {
    const url = encodeURI(`${this.BASE_URL}/pickups/count`);
    return firstValueFrom(this.http.get(url))
      .then(serializeReservationsAndPickupsCount).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.PICKUPS_COUNT, reason);
      });
  }

  async markPickupAsCompleted(orderId: number, userId: number): Promise<PickedUpResponse> {
    const url = encodeURI(`${this.BASE_URL}/pickups/${orderId}/picked_up`);
    return firstValueFrom(this.http.post(url, { user_id: userId})).then(serializePickedUpResponse).then(res => res).catch(reason => {
      throw new BackendServiceError(EndpointErrorsDescriptions.PICKUPS_PICKED_UP, reason);
    });
  }

  addReservationToCart(
    reservation_id: number,
    user_id: number,
    products: ReservationProduct[],
    email: string,
    accepts_newsletter: boolean
  ): Promise<Cart> {
    const url = encodeURI(`${this.BASE_URL}/cart/add`);
    const payload = {
      reservation_id,
      user_id,
      products,
      is_reservation: true,
      pos_customer: { email, accepts_newsletter },
    };

    return firstValueFrom(this.http.post(url, payload, { withCredentials: true }))
      .then(serializeCart)
      .then(res => res.cart).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CART_ADD, reason);
      });
  }

  async callPrintEpsonIhub(payload: { device_id: string; receipt_url: string }): Promise<any> {
    const url = encodeURI(`${this.EXTERNAL_API_URL}/queue_epos_print_job`);
    return firstValueFrom(this.http.post(url, payload)).catch(reason => {
      throw new BackendServiceError(EndpointErrorsDescriptions.EPOS_PRINT, reason);
    });
  }

  async callPrintPrinterbox(payload: { device_id: string; receipt_url: string }): Promise<any> {
    const url = encodeURI(`${this.EXTERNAL_API_URL}/print_to_local`);
    return firstValueFrom(this.http.post(url, payload)).catch(reason => {
      throw new BackendServiceError(EndpointErrorsDescriptions.PRINT_TO_LOCAL, reason);
    });
  }

  async fetchPrinterStatus(deviceId: string): Promise<PrinterStatus> {
    const url = encodeURI(`${this.EXTERNAL_API_URL}/printer_status`);
    return firstValueFrom(this.http.get(url, { params: { device_id: deviceId } }))
      .then(serializePrinterStatus).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.PRINTER_STATUS, reason);
      });
  }

  async cancelQueuedPrinterJobs(deviceId: string): Promise<any> {
    const url = encodeURI(`${this.EXTERNAL_API_URL}/clear_print_jobs`);
    return firstValueFrom(this.http.post(url, { device_id: deviceId })).catch(reason => {
      throw new BackendServiceError(EndpointErrorsDescriptions.CLEAR_PRINT_JOBS, reason);
    });
  }

  async callPaymentAuthorization(id: string, payload: { amount: number; payment_method_id: string }): Promise<AuthorizationResponse> {
    const url = encodeURI(`${this.BASE_URL}/cash-box/${id}/payment/authorization`);
    return firstValueFrom(this.http.post(url, payload))
      .then(serializePaymentAuthorization).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CASHBOX_PAYMENT_AUTHORIZATION, reason);
      });
  }

  async callRefund(id: string, payload: { amount: number; transaction_id: string; payment_method_id: string}): Promise<RefundResponse> {
    const url = encodeURI(`${this.BASE_URL}/cash-box/${id}/payment/refund`);
    return firstValueFrom(this.http.post(url, payload))
      .then(serializePaymentRefund).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CASHBOX_PAYMENT_REFUND, reason);
      });
  }

  async callReversal(id: string, payload: { transaction_id: string; payment_method_id: string }): Promise<ReversalResponse> {
    const url = encodeURI(`${this.BASE_URL}/cash-box/${id}/payment/reversal`);
    return firstValueFrom(this.http.post(url, payload))
      .then(serializePaymentReversal).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CASHBOX_PAYMENT_REVERSAL, reason);
      });
  }

  async callEndOfDay(id: string, payload: { payment_method_id: string }): Promise<EndOfDayResponse> {
    const url = encodeURI(`${this.BASE_URL}/cash-box/${id}/payment/end_of_day`);
    return firstValueFrom(this.http.post(url, payload))
      .then(serializeEndOfDay).catch(reason => {
        throw new BackendServiceError(EndpointErrorsDescriptions.CASHBOX_PAYMENT_END_OF_DAY, reason);
      });
  }

  async deleteReservation(reservationId: number): Promise<any> {
    const url = encodeURI(`${this.BASE_URL}/pickups/cancel_reservation/${reservationId}`);

    return firstValueFrom(this.http.post(url, null)).catch(reason => {
      throw new BackendServiceError(EndpointErrorsDescriptions.PICKUPS_DELETE, reason);
    });
  }
}

export class BackendServiceError extends Error {
  constructor(public readonly endpointErrorsDescription : EndpointErrorsDescriptions, public readonly wrapped: Error) {
    super();
  }
}
